import React, { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoginHeaderOperational from './LoginHeaderOperational';
import Sidebar_Progress from '../layouts/Operational_Sidebar_Progress.component';
import { API_URL } from '../../constant/API_Settings';
import { fetchReportJson, reportNumberFormat, downloadReportPdf, formatReportDate, formatReportDateRange } from '../../utils/reportFormat';
import './other-reports/report-table.css';
import logo from '../../img/logo.svg';
import {
  OtherReportActionButtons,
  OtherReportFilterCard,
  OtherReportFilterSection,
  ReportDateRangeFields,
  OtherReportLoader,
  OtherReportPageHeader,
  OtherReportResultsZone,
  OtherReportSearchableCheckList,
  OtherReportShell,
  OtherReportToolbar,
} from './other-reports/OtherReportShared';

const hasShortExcessTotals = (totals) =>
  Number(totals?.short || 0) > 0 || Number(totals?.excess || 0) > 0;

const ShortExcessReport = (props) => {
  const [meta, setMeta] = useState({ employees: [] });
  const [inputValues, setInputValues] = useState({ employees: [] });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchReportJson(`${API_URL}/short_excess_statement/meta`)
      .then(setMeta)
      .catch((error) => swal('Short Excess Report', error.message, 'error'));
  }, []);

  const toggleEmployee = (id) => {
    const selected = new Set((inputValues.employees || []).map(String));
    const key = String(id);
    if (selected.has(key)) {
      selected.delete(key);
    } else {
      selected.add(key);
    }
    setInputValues({ ...inputValues, employees: Array.from(selected) });
  };

  const setEmployees = (ids) => {
    setInputValues({ ...inputValues, employees: (ids || []).map(String) });
  };

  const validateFilters = () => {
    if (!inputValues.start_date || !inputValues.end_date) {
      swal('Please select start and end dates', '', 'warning');
      return false;
    }
    if (!inputValues.employees?.length) {
      swal('Please select at least one employee', '', 'warning');
      return false;
    }
    return true;
  };

  const buildPayload = () => ({
    from_date: inputValues.start_date,
    to_date: inputValues.end_date,
    employees: inputValues.employees,
  });

  const loadReport = async () => {
    if (!validateFilters()) {
      return;
    }

    setLoading(true);
    setReport(null);
    try {
      const data = await fetchReportJson(`${API_URL}/short_excess_statement`, buildPayload());
      setReport(data);
    } catch (error) {
      swal('Short Excess Report', error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    if (!validateFilters()) {
      return;
    }

    setPdfLoading(true);
    try {
      await downloadReportPdf(
        `${API_URL}/short_excess_statement/pdf`,
        buildPayload(),
        'short_excess_statement.pdf',
      );
    } catch (error) {
      swal('Short Excess Report', error.message, 'error');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="main-container page-wrap">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={14} operation={3} op={3} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title=" " />
          <OtherReportShell>
            <OtherReportPageHeader title="Short Excess Statement" badge="Reports" />
            <OtherReportFilterCard>
              <OtherReportToolbar
                actionsWide
                dates={(
                  <>
                    <ReportDateRangeFields
                      startDate={inputValues.start_date}
                      endDate={inputValues.end_date}
                      onChange={({ start_date, end_date }) => setInputValues({
                        ...inputValues,
                        start_date,
                        end_date,
                      })}
                    />
                  </>
                )}
                actions={(
                  <OtherReportActionButtons
                    loading={loading}
                    pdfLoading={pdfLoading}
                    onSubmit={loadReport}
                    onDownloadPdf={downloadPdf}
                  />
                )}
              />
              <OtherReportFilterSection title="Employees" description="Select one or more employees to include.">
                <OtherReportSearchableCheckList
                  items={meta.employees || []}
                  selectedIds={inputValues.employees || []}
                  onToggle={toggleEmployee}
                  onChange={setEmployees}
                  searchPlaceholder="Search employees…"
                  getId={(item) => String(item.id)}
                  getLabel={(item) => item.name}
                />
              </OtherReportFilterSection>
            </OtherReportFilterCard>
            <OtherReportResultsZone>
              {loading && <OtherReportLoader message="Generating short excess statement" />}
              {report && !loading && (
              <div className="form-wrap balace-sheetwrap">
                <div className="row mb-3">
                  <div className="col-md">
                    <h2 className="table-title mb-1">{report.pump_name}</h2>
                    <h4 className="table-title mb-1">Short Excess Statement</h4>
                    <h5>{formatReportDateRange(report.start_date, report.end_date)}</h5>
                    <img src={logo} className="img-fluid float-end" width="100" alt="logo" />
                  </div>
                </div>

                {report.zero_short_excess ? (
                  <p className="text-muted">Employees with zero short/excess: {report.zero_short_excess}</p>
                ) : null}

                <table className="table border">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Employee name</th>
                      <th className="report-amount-header">Short (Rs)</th>
                      <th className="report-amount-header">Excess (Rs)</th>
                      <th className="report-amount-header">Net short/excess</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report.employee_groups || []).flatMap((group, groupIndex) => {
                      const detailRows = (group.rows || []).map((row, rowIndex) => (
                        <tr key={`${group.employee_name}-${groupIndex}-${rowIndex}`}>
                          <td>{formatReportDate(row.op_date)}</td>
                          <td>{row.name || group.employee_name}</td>
                          <td className="report-amount">{reportNumberFormat(row.short)}</td>
                          <td className="report-amount">{reportNumberFormat(row.excess)}</td>
                          <td className="report-amount">{reportNumberFormat(row.net_amount)}</td>
                        </tr>
                      ));

                      if (!hasShortExcessTotals(group.totals)) {
                        return detailRows;
                      }

                      return [
                        ...detailRows,
                        <tr key={`total-${groupIndex}`} className="fw-bold table-secondary">
                          <td />
                          <td>{group.employee_name} - Total</td>
                          <td className="report-amount">{reportNumberFormat(group.totals.short)}</td>
                          <td className="report-amount">{reportNumberFormat(group.totals.excess)}</td>
                          <td className="report-amount">{reportNumberFormat(group.totals.net_total)}</td>
                        </tr>,
                      ];
                    })}
                  </tbody>
                </table>

                {report.overall && (
                  <div className="alert alert-info">
                    Overall short: {reportNumberFormat(report.overall.overall_short)} | Overall excess:{' '}
                    {reportNumberFormat(report.overall.overall_excess)} | Net:{' '}
                    {reportNumberFormat(report.overall.overall_short_excess)}
                  </div>
                )}
              </div>
              )}
            </OtherReportResultsZone>
          </OtherReportShell>
        </div>
      </div>
    </div>
  );
};

export default ShortExcessReport;
